import React from 'react';
import { Target, Heart, Award } from 'lucide-react';

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-[#0D2847] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-4">About Rudra Essence</h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Bringing science-backed wellness solutions to elevate your health journey
          </p>
        </div>
      </section>

      {/* Mission */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#0D2847] mb-6">Our Mission</h2>
              <p className="text-gray-600 mb-4 leading-relaxed">
                At Rudra Essence, we are dedicated to bringing the most advanced and scientifically proven wellness solutions to those seeking optimal health and vitality. We believe that true wellness is rooted in science, quality, and authenticity.
              </p>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Our carefully curated portfolio represents three distinct yet complementary approaches to wellness: molecular hydrogen therapy for cellular optimization, premium cosmeceutical skincare for visible results, and infrared therapy for holistic healing.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Each brand we represent shares our commitment to excellence, backed by rigorous research and proven results.
              </p>
            </div>
            <div className="rounded-lg overflow-hidden shadow-xl">
              <img 
                src="https://images.unsplash.com/photo-1488345979593-09db0f85545f?w=800" 
                alt="Wellness"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0D2847] mb-12 text-center">Our Values</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-[#0D2847] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Target className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Scientific Excellence</h3>
              <p className="text-gray-600">
                We only represent brands backed by rigorous scientific research and clinical validation. Every product delivers measurable results.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-[#0D2847] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Customer Well-being</h3>
              <p className="text-gray-600">
                Your health and satisfaction are our priority. We provide personalized guidance to help you find the perfect wellness solutions.
              </p>
            </div>
            <div className="text-center">
              <div className="bg-[#0D2847] w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-semibold mb-3">Premium Quality</h3>
              <p className="text-gray-600">
                We partner with industry-leading brands known for innovation, quality, and proven effectiveness in their respective fields.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Brands */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#0D2847] mb-12 text-center">The Brands We Represent</h2>
          <div className="space-y-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold text-[#0D2847] mb-4">Axiom H2</h3>
                <p className="text-gray-600 mb-3">
                  Pioneering molecular hydrogen technology with medical-grade systems designed to optimize health at the cellular level.
                </p>
                <p className="text-gray-600">
                  Axiom H2 combines aerospace-engineer quality with scientific precision to deliver the most advanced molecular hydrogen systems in the world.
                </p>
              </div>
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img src="https://customer-assets.emergentagent.com/job_complete-web-dev-1/artifacts/s34t1beg_image.png" alt="Axiom H2" className="w-full h-64 object-cover" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1 rounded-lg overflow-hidden shadow-lg">
                <img src="https://customer-assets.emergentagent.com/job_complete-web-dev-1/artifacts/p9h3iuow_image.png" alt="Beauté Pacifique" className="w-full h-64 object-cover" />
              </div>
              <div className="order-1 md:order-2">
                <h3 className="text-2xl font-bold text-[#0D2847] mb-4">Beauté Pacifique</h3>
                <p className="text-gray-600 mb-3">
                  Danish cosmeceutical skincare with scientifically documented results since 1997.
                </p>
                <p className="text-gray-600">
                  The first skincare brand in the world to objectively document significant skin rejuvenation through ultrasound scanning.
                </p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-2xl font-bold text-[#0D2847] mb-4">Therasage</h3>
                <p className="text-gray-600 mb-3">
                  Over 25 years of trusted red light and infrared technology for holistic wellness and healing.
                </p>
                <p className="text-gray-600">
                  From portable infrared saunas to healing pads, Therasage products feature patented gemstone technology and grounding for enhanced benefits.
                </p>
              </div>
              <div className="rounded-lg overflow-hidden shadow-lg">
                <img src="https://customer-assets.emergentagent.com/job_complete-web-dev-1/artifacts/bix546un_image.png" alt="Therasage" className="w-full h-64 object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
